// src/pages/POSProductList.js
import React, { useState, useEffect, useMemo, useRef } from "react";
// import InfiniteScroll from "react-infinite-scroll-component";
import { usePOSProductsContext } from "../contexts/POSProductContext";
import ProductCard from "../../CommonComponents/ProductCard";
import { useCart } from "../contexts/cartContext";
import axiosInstance from "../../../services/axiosInstance";
import { authStorage } from "../../../utils/authStorage";
import SearchInput from "../../CommonComponents/SearchInput";
import BulkEditButton from "../../CommonComponents/BulkEditButton";
import EmptyState from "../../CommonComponents/EmptyState";
import { ChevronDown, Flame, ShoppingCart, Star, TrendingUp, WifiOff } from "lucide-react";

// Skeleton loader
const ProductSkeletonGrid = ({ count, sidebarCollapsed = false }) => (
    <div className="relative px-4 pt-4 animate-pulse">
        <div
            className="sticky top-0 z-40 mb-4 flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white/95 px-1 py-2"
            style={{ backdropFilter: 'blur(2px)' }}
        >
            <div className="h-12 flex-1 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
            <div className="h-11 w-24 shrink-0 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
        </div>

        <div className="mb-5 w-full">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/40 shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-amber-100/80" />
                        <div className="space-y-2">
                            <div className="h-3 w-28 rounded-full bg-slate-200" />
                            <div className="h-2.5 w-44 rounded-full bg-slate-100" />
                        </div>
                    </div>
                    <div className="hidden h-8 w-24 rounded-full bg-slate-100 sm:block" />
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <div key={`top-skeleton-${idx}`} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                                <div className="h-24 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                                <div className="absolute left-2 top-2 h-6 w-12 rounded-full bg-slate-800/85" />
                            </div>
                            <div className="mt-3 space-y-2">
                                <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                                <div className="h-3 w-2/5 rounded-full bg-slate-100" />
                                <div className="h-3 w-1/3 rounded-full bg-slate-100" />
                            </div>
                            <div className="mt-4 h-9 rounded-xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
                        </div>
                    ))}
                </div>
            </div>
        </div>

        <div
            className={`${sidebarCollapsed ? 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6' : 'grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-4xl'}`}
        >
            {Array.from({ length: count }).map((_, idx) => (
                <div
                    key={idx}
                    className="overflow-hidden rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm"
                >
                    <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                        <div className="h-36 sm:h-40 md:h-44 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                        <div className="absolute left-3 top-3 h-8 w-20 rounded-full bg-slate-800/90" />
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                        <div className="h-3 w-1/2 rounded-full bg-slate-100" />
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="h-7 rounded-full bg-emerald-50" />
                        <div className="h-7 rounded-full bg-amber-50" />
                    </div>
                    <div className="mt-4 h-10 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
                </div>
            ))}
        </div>
    </div>
);

function POSProductList({ sidebarCollapsed = false }) {
    // const { user } = useAuth();
    // Only use addToCart from cart context.
    const { addToCart } = useCart();
    const { products, loading, error, fetchProducts } = usePOSProductsContext();
    // Debug: log products and their stock to verify inventory source
    React.useEffect(() => {
        if (products && products.length > 0) {
            console.log("[POSProductList] Products for this user:", products.map(p => ({ id: p.id, name: p.name, stock: p.stock })));
        }
    }, [products]);
    const [searchTerm, setSearchTerm] = useState("");
        // Reset to first page when search term changes
        useEffect(() => {
            setCurrentPage(1);
        }, [searchTerm]);
    const itemsPerPage = 20;
    const [currentPage, setCurrentPage] = useState(1);

    const [viewMode] = useState("grid");
    const [massEditMode, setMassEditMode] = useState(false);
    const [topProducts, setTopProducts] = useState([]);
    const [topProductsCollapsed, setTopProductsCollapsed] = useState(true);
    const [topProductsRefreshKey, setTopProductsRefreshKey] = useState(0);
    const productListTopRef = useRef(null);

    // Track quantities per product in bulk mode
    const [massEditQuantities, setMassEditQuantities] = useState({});

    // Filter products by search and sort: in-stock first
    const filteredProducts = useMemo(() => {
        return products
            .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((a, b) => {
                const aInStock = (a.stock || 0) > 0;
                const bInStock = (b.stock || 0) > 0;
                if (aInStock === bInStock) return a.name.localeCompare(b.name);
                return aInStock ? -1 : 1;
            });
    }, [products, searchTerm]);
    const productSuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return products
            .map((product) => product.name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [products, searchTerm]);

    useEffect(() => {
        const handleTopProductsRefresh = () => {
            setTopProductsRefreshKey((prev) => prev + 1);
        };

        window.addEventListener("pos-order-created", handleTopProductsRefresh);
        return () => window.removeEventListener("pos-order-created", handleTopProductsRefresh);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchTopProducts = async () => {
            try {
                const user = authStorage.getUser();
                const branchId = user?.branch_id;
                const topProductsRes = await axiosInstance.get("/admin/orders/analytics", {
                    params: branchId ? { branch_id: branchId } : undefined,
                });
                const topFive = (topProductsRes.data?.topProducts || []).slice(0, 5);
                if (!isMounted) return;
                setTopProducts(topFive);
            } catch (fetchError) {
                console.error("[POSProductList] Failed to load top products:", fetchError);
                if (isMounted) {
                    setTopProducts([]);
                }
            }
        };

        fetchTopProducts();

        return () => {
            isMounted = false;
        };
    }, [topProductsRefreshKey]);

    useEffect(() => {
        if (searchTerm.trim()) {
            setTopProductsCollapsed(true);
        }
    }, [searchTerm]);

    const topProductCards = useMemo(() => {
        return topProducts.map((item, index) => {
            const matchedProduct = products.find((product) => String(product.id) === String(item.product_id));
            return {
                id: item.product_id || `top-${index}`,
                name: item.product || matchedProduct?.name || `Product ${index + 1}`,
                sold: Number(item.totalQty || 0),
                image: matchedProduct?.image || null,
                price: matchedProduct?.price ?? null,
                stock: matchedProduct?.stock ?? 0,
                product: matchedProduct || null,
            };
        });
    }, [products, topProducts]);

    // Pagination logic
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(start, start + itemsPerPage);
    }, [filteredProducts, currentPage]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        setCurrentPage(page);
        window.requestAnimationFrame(() => {
            productListTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };


    // Toggle mass edit mode
    const handleToggleMassEdit = () => {
        setMassEditMode((prev) => !prev);

        if (!massEditMode) {
            // Initialize quantities for bulk edit, but exclude products with stock <= 0
            const initialQuantities = {};
            products
                .filter((p) => p.stock > 0) // only include products with stock
                .forEach((p) => (initialQuantities[p.id] = 0));
            setMassEditQuantities(initialQuantities);
        }
    };

    // Handle quantity change for mass edit mode
    const handleQuantityChange = (productId, value) => {
        setMassEditQuantities(prev => ({ ...prev, [productId]: value }));
    };

    // ...existing code...

    if (loading) return <ProductSkeletonGrid count={itemsPerPage} sidebarCollapsed={sidebarCollapsed} />;

    if (error)
        return (
            <EmptyState
                icon={WifiOff}
                title="Failed to Load Products"
                message={error.message || "Check your internet connection and try again."}
                onRetry={fetchProducts}
                retryLabel="Retry"
            />
        );

    return (
        <div ref={productListTopRef} className="relative px-4 pt-4">
            {/* Fixed search and bulk edit at top of grid container */}
            <div
                className="sticky top-0 z-40 mb-4 flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white/95 px-1 py-2"
                style={{ backdropFilter: 'blur(2px)' }}
            >
                <div className="flex-1">
                    <SearchInput
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onSuggestionSelect={(productName) => setSearchTerm(productName)}
                        suggestions={productSuggestions}
                        placeholder="Search products..."
                    />
                </div>
                <div className="shrink-0">
                    <BulkEditButton
                        onClick={handleToggleMassEdit}
                        title={massEditMode ? "Done" : "Bulk Edit"}
                        isActive={massEditMode}
                    />
                </div>
            </div>

            {topProductCards.length > 0 && (
                <section className="mb-5 w-full">
                    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/50 shadow-sm">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                        <Flame className="h-4 w-4" />
                                    </span>
                                    <h2 className="text-sm font-semibold text-slate-900">Top 5 Products</h2>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">
                                    Best-selling items based on recorded sales.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                    <TrendingUp className="h-3.5 w-3.5" />
                                    Popular now
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setTopProductsCollapsed((prev) => !prev)}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                                >
                                    {topProductsCollapsed ? "Show" : "Hide"}
                                    <ChevronDown className={`h-3.5 w-3.5 transition ${topProductsCollapsed ? "-rotate-90" : "rotate-0"}`} />
                                </button>
                            </div>
                        </div>

                        {!topProductsCollapsed && (
                            <div
                                className={`grid gap-3 p-4 ${
                                    sidebarCollapsed
                                        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
                                        : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-5"
                                }`}
                            >
                                {topProductCards.map((product, index) => (
                                    <div
                                        key={product.id}
                                        className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                    >
                                        <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="h-24 w-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-24 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400">
                                                    <ShoppingCart className="h-7 w-7" />
                                                </div>
                                            )}

                                            <div className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-2 py-1 text-[11px] font-semibold text-white">
                                                <Star className="h-3 w-3 text-amber-300" />
                                                #{index + 1}
                                            </div>
                                        </div>

                                        <div className="mt-3 min-h-[88px] space-y-1">
                                            <p className="line-clamp-2 text-sm font-semibold text-slate-900">
                                                {product.name}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                {product.sold} unit{product.sold === 1 ? "" : "s"} sold
                                            </p>
                                            {product.price !== null && (
                                                <p className="text-xs font-medium text-slate-700">
                                                    ₱{Number(product.price).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </p>
                                            )}
                                            {product.stock > 0 && (
                                                <p className="text-xs font-medium text-emerald-600">
                                                    {product.stock} left in stock
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => product.product && product.stock > 0 && addToCart(product.product)}
                                            disabled={!product.product || product.stock <= 0}
                                            className={`mt-auto flex w-full items-center justify-center rounded-xl px-3 py-2 text-xs font-semibold transition ${product.product && product.stock > 0
                                                    ? 'text-white shadow-sm hover:opacity-90'
                                                    : 'cursor-not-allowed bg-slate-100 text-slate-400'
                                                }`}
                                            style={product.product && product.stock > 0 ? { background: 'var(--color-primary)' } : {}}
                                        >
                                            {product.product && product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Pagination Controls - Fixed at bottom INSIDE product grid container */}
            <div className="relative">
                <div
                    className={`${viewMode === "grid" ? `${sidebarCollapsed ? 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6' : 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'}` : "flex flex-col gap-2"}`}
                >
                    {paginatedProducts.length > 0 ? (
                        paginatedProducts.map((product, idx) => (
                            <ProductCard
                                key={`${product.id}-${idx}`}
                                product={product}
                                massEditMode={massEditMode}
                                viewMode={viewMode}
                                quantity={massEditQuantities[product.id] || 0}
                                onQuantityChange={(id, val) => handleQuantityChange(id, val)}
                                showAddToCart={true}
                                onAddToCart={addToCart}
                            />
                        ))
                    ) : (
                        <EmptyState icon={ShoppingCart} message="No products found" />
                    )}
                </div>

                {/* Uniform pagination style below grid, matching POSProductsTable */}
                {totalPages > 1 && (
                    <div className="sticky bottom-0 z-20 w-full flex flex-col items-center gap-0 mt-6 bg-white/95 border-t border-gray-200 shadow p-0 pt-2">
                        <div className="flex items-center gap-1">
                            <button
                                className={`px-2 py-1 rounded-md text-xs font-medium border focus:outline-none ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'text-white'}`}
                                style={currentPage === 1 ? {} : { background: 'var(--color-primary)' }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>
                            {Array.from({ length: totalPages }).map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handlePageChange(idx + 1)}
                                    className={`px-2 py-1 rounded-md text-xs font-semibold border focus:outline-none ${currentPage === idx + 1 ? 'text-white' : 'bg-white hover:bg-gray-100'}`}
                                    style={currentPage === idx + 1
                                        ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                                        : { color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                                    }
                                >
                                    {idx + 1}
                                </button>
                            ))}
                            <button
                                className={`px-2 py-1 rounded-md text-xs font-medium border focus:outline-none ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'text-white'}`}
                                style={currentPage === totalPages ? {} : { background: 'var(--color-primary)' }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                        <p className="text-sm mb-0 pb-0 leading-tight" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                            Showing {filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
                            – {Math.min(currentPage * itemsPerPage, filteredProducts.length)}{' '}
                            of {filteredProducts.length} items | Page {currentPage} of {totalPages}
                        </p>
                    </div>
                )}
            </div>

            {/* ...product grid and fixed pagination now handled above... */}
        </div>
    );
}
export default POSProductList;
